export const APP_ROUTES = {
  LOGIN: '/login',
  HOME: '/dashboard'
};

export const ADMIN_PERMISSIONS = {
  DASHBOARD_VIEW: 'dashboard.view',
  KYC_REVIEW: 'kyc.review',
  CONFIG_MANAGE: 'config.manage',
  LOGS_VIEW: 'logs.view',
  MONITORING_VIEW: 'monitoring.view',
  ADMINS_MANAGE: 'admins.manage',
  QUESTION_BANKS_VIEW: 'question_banks.view',
  QUESTION_BANKS_MANAGE: 'question_banks.manage',
  NOTIFICATIONS_VIEW: 'notifications.view',
  NOTIFICATIONS_MANAGE: 'notifications.manage',
  BILLING_VIEW: 'billing.view',
  BILLING_MANAGE: 'billing.manage',
  TICKETS_VIEW: 'tickets.view',
  TICKETS_MANAGE: 'tickets.manage'
};

export const ADMIN_MODULES = [
  {
    key: 'kyc',
    name: 'Employer KYC',
    path: '/employers/kyc',
    permission: ADMIN_PERMISSIONS.KYC_REVIEW,
    description: 'Employer verification queue, approvals, rejections, documents, and reviewer notes.'
  },
  {
    key: 'configurations',
    name: 'Platform Configs',
    path: '/configurations',
    permission: ADMIN_PERMISSIONS.CONFIG_MANAGE,
    description: 'Global and employer-level matching weights, platform rules, and taxonomy controls.'
  },
  {
    key: 'taxonomy',
    name: 'Tag Taxonomy',
    path: '/configurations/taxonomy',
    permission: ADMIN_PERMISSIONS.CONFIG_MANAGE,
    description: 'Canonical tags, aliases, suggested tags, duplicate prevention, and taxonomy review operations.'
  },
  {
    key: 'employer-logs',
    name: 'Employer Logs',
    path: '/logs/employers',
    permission: ADMIN_PERMISSIONS.LOGS_VIEW,
    description: 'Employer account, hiring, KYC, offer, and billing activity trails.'
  },
  {
    key: 'candidate-logs',
    name: 'Candidate Logs',
    path: '/logs/candidates',
    permission: ADMIN_PERMISSIONS.LOGS_VIEW,
    description: 'Candidate profile, matching, assessment, interview, and notification activity trails.'
  },
  {
    key: 'monitoring',
    name: 'Monitoring',
    path: '/monitoring',
    permission: ADMIN_PERMISSIONS.MONITORING_VIEW,
    description: 'Live user activity, matching movement, performance, inactivity risk, and system health monitoring.'
  },
  {
    key: 'job-insights',
    name: 'Job AI Insights',
    path: '/jobs/insights',
    permission: ADMIN_PERMISSIONS.MONITORING_VIEW,
    description: 'Per-job match timing, matching funnel counts, AI token usage, and estimated provider spend.'
  },
  {
    key: 'admins',
    name: 'Admins & RBAC',
    path: '/configurations/admins',
    permission: ADMIN_PERMISSIONS.ADMINS_MANAGE,
    description: 'Admin users, roles, permissions, invitations, and access lifecycle controls.'
  },
  {
    key: 'question-banks',
    name: 'Question Banks',
    path: '/configurations/question-banks',
    permission: ADMIN_PERMISSIONS.QUESTION_BANKS_VIEW,
    description: 'Governed candidate, employer, and job questions with versioning, answer snapshots, and revision history.'
  },
  {
    key: 'legal-documents',
    name: 'Legal Pages',
    path: '/configurations/legal-documents',
    permission: ADMIN_PERMISSIONS.CONFIG_MANAGE,
    description: 'Admin-written terms of service and privacy policy pages for employer and candidate applications.'
  },
  {
    key: 'notifications',
    name: 'Communications',
    path: '/communications/notifications',
    permission: ADMIN_PERMISSIONS.NOTIFICATIONS_VIEW,
    description: 'Candidate in-app and WhatsApp campaigns, employer email campaigns, recipient targeting, delivery status, and queue health.'
  },
  {
    key: 'whatsapp-sender',
    name: 'WhatsApp Sender',
    path: '/communications/whatsapp',
    permission: ADMIN_PERMISSIONS.NOTIFICATIONS_VIEW,
    description: 'Baileys sender status, QR pairing, connected-number visibility, and sender disconnect controls.'
  },
  {
    key: 'billing',
    name: 'Billing',
    path: '/finance/billing',
    permission: ADMIN_PERMISSIONS.BILLING_VIEW,
    description: 'Candidate billing status, payments, access expiry, billing offers, regional offers, and revenue controls.'
  },
  {
    key: 'support-tickets',
    name: 'Support Tickets',
    path: '/support/tickets',
    permission: ADMIN_PERMISSIONS.TICKETS_VIEW,
    description: 'Customer support queue for candidate, employer, and admin-created tickets, assignment, ETA, and resolution workflows.'
  }
];

export const ADMIN_NAVIGATION_GROUPS = [
  {
    key: 'dashboard',
    name: 'Dashboard',
    path: '/dashboard',
    icon: 'BarChart3',
    permission: ADMIN_PERMISSIONS.DASHBOARD_VIEW
  },
  {
    key: 'employers',
    name: 'Employers',
    icon: 'Building2',
    children: [
      { key: 'employers-list', name: 'Employer list', path: '/employers', icon: 'Building2', permission: ADMIN_PERMISSIONS.MONITORING_VIEW },
      { key: 'employer-kyc', name: 'Employer KYC', path: '/employers/kyc', icon: 'ClipboardCheck', permission: ADMIN_PERMISSIONS.KYC_REVIEW }
    ]
  },
  {
    key: 'candidates',
    name: 'Candidates',
    icon: 'UsersRound',
    children: [
      { key: 'candidates-list', name: 'Candidate list', path: '/candidates', icon: 'UsersRound', permission: ADMIN_PERMISSIONS.MONITORING_VIEW }
    ]
  },
  {
    key: 'jobs',
    name: 'Jobs',
    icon: 'Briefcase',
    children: [
      { key: 'jobs-list', name: 'Job list', path: '/jobs', icon: 'Briefcase', permission: ADMIN_PERMISSIONS.MONITORING_VIEW },
      { key: 'job-insights', name: 'Job insights', path: '/jobs/insights', icon: 'BrainCircuit', permission: ADMIN_PERMISSIONS.MONITORING_VIEW }
    ]
  },
  {
    key: 'hiring',
    name: 'Hiring flow',
    icon: 'CalendarClock',
    children: [
      { key: 'interviews', name: 'Interviews', path: '/interviews', icon: 'CalendarClock', permission: ADMIN_PERMISSIONS.MONITORING_VIEW },
      { key: 'assessments', name: 'Assessments', path: '/assessments', icon: 'ClipboardList', permission: ADMIN_PERMISSIONS.MONITORING_VIEW },
      { key: 'offers', name: 'Offers', path: '/offers', icon: 'FileSignature', permission: ADMIN_PERMISSIONS.MONITORING_VIEW }
    ]
  },
  {
    key: 'communications',
    name: 'Communications',
    icon: 'BellRing',
    children: [
      { key: 'notifications', name: 'Campaigns', path: '/communications/notifications', icon: 'BellRing', permission: ADMIN_PERMISSIONS.NOTIFICATIONS_VIEW },
      { key: 'whatsapp-sender', name: 'WhatsApp sender', path: '/communications/whatsapp', icon: 'Smartphone', permission: ADMIN_PERMISSIONS.NOTIFICATIONS_VIEW }
    ]
  },
  {
    key: 'support',
    name: 'Support',
    icon: 'LifeBuoy',
    children: [
      { key: 'support-tickets', name: 'Tickets', path: '/support/tickets', icon: 'LifeBuoy', permission: ADMIN_PERMISSIONS.TICKETS_VIEW }
    ]
  },
  {
    key: 'configuration',
    name: 'Configuration',
    icon: 'SlidersHorizontal',
    children: [
      { key: 'configurations', name: 'Platform configs', path: '/configurations', icon: 'SlidersHorizontal', permission: ADMIN_PERMISSIONS.CONFIG_MANAGE },
      { key: 'taxonomy', name: 'Tag taxonomy', path: '/configurations/taxonomy', icon: 'Tags', permission: ADMIN_PERMISSIONS.CONFIG_MANAGE },
      { key: 'question-banks', name: 'Question banks', path: '/configurations/question-banks', icon: 'BookOpenCheck', permission: ADMIN_PERMISSIONS.QUESTION_BANKS_VIEW },
      { key: 'legal-documents', name: 'Legal pages', path: '/configurations/legal-documents', icon: 'FileText', permission: ADMIN_PERMISSIONS.CONFIG_MANAGE },
      { key: 'admins', name: 'Admins & RBAC', path: '/configurations/admins', icon: 'UserCog', permission: ADMIN_PERMISSIONS.ADMINS_MANAGE }
    ]
  },
  {
    key: 'finance',
    name: 'Finance',
    icon: 'CreditCard',
    children: [
      { key: 'billing', name: 'Billing', path: '/finance/billing', icon: 'CreditCard', permission: ADMIN_PERMISSIONS.BILLING_VIEW }
    ]
  },
  {
    key: 'logs',
    name: 'Logs',
    icon: 'FileClock',
    children: [
      { key: 'logs-overview', name: 'Log overview', path: '/logs', icon: 'FileClock', permission: ADMIN_PERMISSIONS.LOGS_VIEW },
      { key: 'employer-logs', name: 'Employer logs', path: '/logs/employers', icon: 'Building2', permission: ADMIN_PERMISSIONS.LOGS_VIEW },
      { key: 'candidate-logs', name: 'Candidate logs', path: '/logs/candidates', icon: 'UsersRound', permission: ADMIN_PERMISSIONS.LOGS_VIEW },
      { key: 'job-logs', name: 'Job logs', path: '/logs/jobs', icon: 'Briefcase', permission: ADMIN_PERMISSIONS.LOGS_VIEW },
      { key: 'interview-logs', name: 'Interview logs', path: '/logs/interviews', icon: 'CalendarClock', permission: ADMIN_PERMISSIONS.LOGS_VIEW },
      { key: 'assessment-logs', name: 'Assessment logs', path: '/logs/assessments', icon: 'ClipboardList', permission: ADMIN_PERMISSIONS.LOGS_VIEW },
      { key: 'offer-logs', name: 'Offer logs', path: '/logs/offers', icon: 'FileSignature', permission: ADMIN_PERMISSIONS.LOGS_VIEW }
    ]
  },
  {
    key: 'system',
    name: 'System',
    icon: 'Activity',
    children: [
      { key: 'monitoring', name: 'Monitoring', path: '/monitoring', icon: 'Activity', permission: ADMIN_PERMISSIONS.MONITORING_VIEW }
    ]
  }
];

export const ADMIN_ROUTE_PERMISSIONS = ADMIN_NAVIGATION_GROUPS.flatMap((item) => (
  item.children
    ? item.children.map((child) => ({ path: child.path, permission: child.permission }))
    : [{ path: item.path, permission: item.permission }]
));

export const ADMIN_STUB_PAGES = {
  employers: {
    eyebrow: 'Employers',
    title: 'Employer list',
    description: 'A platform-wide employer directory for company profiles, account state, KYC summary, jobs, billing posture, and recent activity.',
    status: 'Stub ready',
    apiNotes: [
      'Admin dashboard already reports employer profile and KYC counts.',
      'Admin KYC endpoints support employer verification details, document review, and account status updates.',
      'A dedicated admin employer-list API is still needed before this page becomes live data.'
    ],
    sections: [
      { title: 'Expected controls', items: ['Search employers by company, email, country, and KYC state', 'Open employer profile and verification summary', 'Suspend, reactivate, or route account status actions through admin audit'] },
      { title: 'Expected data', items: ['Company profile', 'KYC status', 'Active jobs', 'Hiring activity', 'Billing state', 'Recent admin actions'] }
    ]
  },
  candidates: {
    eyebrow: 'Candidates',
    title: 'Candidate list',
    description: 'A candidate operations page for profile state, CV coverage, visibility, matching status, assessments, interviews, offers, and support review.',
    status: 'Stub ready',
    apiNotes: [
      'Candidate role APIs cover profile, CV, notifications, matches, interviews, and billing from the candidate side.',
      'Admin logs and dashboard currently expose aggregate candidate operational context.',
      'A dedicated admin candidate-list API is still needed for safe cross-candidate management.'
    ],
    sections: [
      { title: 'Expected controls', items: ['Search candidates by name, email, phone, visibility, and readiness', 'Inspect profile/CV/matching history without exposing restricted contact data improperly', 'Open candidate logs and support context'] },
      { title: 'Expected data', items: ['Profile completion', 'Primary CV', 'Visibility state', 'Match activity', 'Billing access', 'Assessments and interviews'] }
    ]
  },
  jobs: {
    eyebrow: 'Jobs',
    title: 'Job list',
    description: 'A platform-wide view of job postings, status, employer ownership, extraction state, matching progress, and operational risk.',
    status: 'Stub ready',
    apiNotes: [
      'Employer role APIs already support job creation, import runs, matching, analytics, assessments, and lifecycle updates.',
      'Admin job insights endpoint exists for per-job AI and matching observability.',
      'A dedicated admin job-list API is still needed for global listing and moderation.'
    ],
    sections: [
      { title: 'Expected controls', items: ['Filter by employer, status, country, extraction state, and matching health', 'Open job detail, matching summary, and audit history', 'Review jobs stuck in draft, import, or matching states'] },
      { title: 'Expected data', items: ['Job status', 'Employer', 'Match count', 'Created/published dates', 'AI extraction state', 'Open offers/interviews'] }
    ]
  },
  interviews: {
    eyebrow: 'Hiring flow',
    title: 'Interviews',
    description: 'A global interview operations page covering scheduled, confirmed, cancelled, completed, and feedback-pending interviews.',
    status: 'Stub ready',
    apiNotes: [
      'Interview APIs exist for employer and candidate flows.',
      'Admin dashboard already reports interview counts and status distributions.',
      'A dedicated admin interview-list API is still needed for global operations.'
    ],
    sections: [
      { title: 'Expected controls', items: ['Filter by status, date window, employer, candidate, and job', 'Open interview details and feedback state', 'Escalate scheduling or no-show issues'] },
      { title: 'Expected data', items: ['Schedule time', 'Status', 'Employer', 'Candidate', 'Job', 'Feedback completion', 'Meeting link presence'] }
    ]
  },
  assessments: {
    eyebrow: 'Hiring flow',
    title: 'Assessments',
    description: 'A platform view of assessments created by employers, candidate attempts, review state, warnings, and scoring outcomes.',
    status: 'Stub ready',
    apiNotes: [
      'Assessment APIs support employer assessment lists, candidate attempts, progress, warnings, termination, submission, and review.',
      'Job-scoped assessment APIs exist for creation, invitations, results, and reviews.',
      'A dedicated admin assessment operations API is still needed for global governance.'
    ],
    sections: [
      { title: 'Expected controls', items: ['Filter by assessment type, employer, job, attempt status, and review state', 'Inspect warnings and terminated attempts', 'Route manual-review queues'] },
      { title: 'Expected data', items: ['Assessment type', 'Job', 'Employer', 'Invited candidates', 'Attempts', 'Warnings', 'Scores', 'Review state'] }
    ]
  },
  offers: {
    eyebrow: 'Hiring flow',
    title: 'Offers',
    description: 'A global offer operations page for offers, negotiations, signed letters, withdrawals, accepted offers, and expired offers.',
    status: 'Stub ready',
    apiNotes: [
      'Offer APIs exist for employer, candidate, offer details, candidate response, signed-letter upload, withdrawals, and negotiation responses.',
      'Admin dashboard exposes offer status distribution and open-offer counts.',
      'A dedicated admin offer-list API is still needed for platform-wide oversight.'
    ],
    sections: [
      { title: 'Expected controls', items: ['Filter by status, employer, candidate, job, currency, and expiration window', 'Inspect negotiation history and signed-letter state', 'Flag stalled or expired offers'] },
      { title: 'Expected data', items: ['Offer status', 'Salary/currency', 'Candidate', 'Employer', 'Job', 'Negotiations', 'Signed letter', 'Expiry'] }
    ]
  },
  logsOverview: {
    eyebrow: 'Logs',
    title: 'Log overview',
    description: 'A single entry point for domain-specific audit trails across employers, candidates, jobs, interviews, assessments, offers, billing, and admin actions.',
    status: 'Stub ready',
    apiNotes: [
      'Admin APIs currently expose employer and candidate audit log endpoints.',
      'Dashboard recent activity already comes from activity logs.',
      'Job, interview, assessment, and offer-specific admin log endpoints still need backend support.'
    ],
    sections: [
      { title: 'Available now', items: ['Employer logs', 'Candidate logs', 'Recent dashboard audit activity'] },
      { title: 'Missing endpoints', items: ['Job logs', 'Interview logs', 'Assessment logs', 'Offer logs', 'Unified log search/export'] }
    ]
  },
  jobLogs: {
    eyebrow: 'Logs',
    title: 'Job logs',
    description: 'Audit trail for job creation, imports, parsing, publishing, status changes, matching runs, and match actions.',
    status: 'Backend endpoint needed',
    apiNotes: ['Employer job lifecycle APIs exist.', 'Admin employer/candidate logs exist.', 'A job-specific admin log endpoint is not exposed yet.'],
    sections: [{ title: 'Expected log events', items: ['Job created/updated/deleted', 'JD import events', 'Publish/unpublish', 'Matching trigger/results', 'Bulk match action'] }]
  },
  interviewLogs: {
    eyebrow: 'Logs',
    title: 'Interview logs',
    description: 'Audit trail for interview scheduling, candidate responses, cancellations, confirmations, and feedback submissions.',
    status: 'Backend endpoint needed',
    apiNotes: ['Interview APIs exist for operational flows.', 'A dedicated admin interview-log endpoint is not exposed yet.'],
    sections: [{ title: 'Expected log events', items: ['Interview scheduled', 'Candidate response', 'Employer confirmation', 'Cancellation', 'Feedback submitted'] }]
  },
  assessmentLogs: {
    eyebrow: 'Logs',
    title: 'Assessment logs',
    description: 'Audit trail for assessment creation, invitations, attempts, warnings, submissions, review, and termination events.',
    status: 'Backend endpoint needed',
    apiNotes: ['Assessment attempt APIs expose the operational behavior.', 'A dedicated admin assessment-log endpoint is not exposed yet.'],
    sections: [{ title: 'Expected log events', items: ['Assessment created/updated', 'Candidate invited', 'Attempt started', 'Warning recorded', 'Attempt submitted', 'Review saved'] }]
  },
  offerLogs: {
    eyebrow: 'Logs',
    title: 'Offer logs',
    description: 'Audit trail for offer creation, viewing, negotiation, candidate response, signed-letter upload, expiry, and withdrawal.',
    status: 'Backend endpoint needed',
    apiNotes: ['Offer APIs support employer and candidate flows.', 'A dedicated admin offer-log endpoint is not exposed yet.'],
    sections: [{ title: 'Expected log events', items: ['Offer created', 'Offer viewed', 'Negotiation response', 'Candidate accepted/declined', 'Signed letter uploaded', 'Offer withdrawn'] }]
  }
};
